import React, { useEffect, useMemo } from "react";
import {
  Alert,
  Drawer,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import TargetOutlinedIcon from "@mui/icons-material/AdsClick";
import ActivityOutlinedIcon from "@mui/icons-material/Celebration";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EqualizerRoundedIcon from "@mui/icons-material/EqualizerRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import { useNavigate, useParams } from "react-router-dom";
import DialogContext from "../contexts/DialogContext";
import { ServiceMapDiagram } from "../components/ServiceMapDiagram";
import { useServiceMap } from "../hooks/query/useServiceMap";
import type { Status } from "../types";
import { HealthScore } from "../components/services/HealthScore";
import { TrafficLight } from "../components/services/TrafficLight";
import { COLORS } from "../theme/theme-options";
import { useDashboard } from "../hooks/query/useDashboard";
import { useServiceEvents } from "../hooks/query/useServiceEvents";
import { useAlerts } from "../hooks/query/useAlerts";
import { useIntegrations } from "../hooks/query/useIntegrations";
import { isCriticalKpiId } from "../criticalKpi";
import {
  buildServicePageViewData,
  scoreColorForScore,
  selectRootServiceId,
} from "../services/servicePageViewService";

const timelineToneByStatus: Record<
  Status,
  {
    severityLabel: string;
    stateLabel: string;
    main: string;
    subtle: string;
    border: string;
  }
> = {
  critical: {
    severityLabel: "Critical",
    stateLabel: "Open",
    main: "#f87171",
    subtle: "rgba(248, 113, 113, 0.16)",
    border: "#ef4444",
  },
  warning: {
    severityLabel: "Medium",
    stateLabel: "Acknowledged",
    main: "#fbbf24",
    subtle: "rgba(251, 191, 36, 0.16)",
    border: "#f59e0b",
  },
  healthy: {
    severityLabel: "Healthy",
    stateLabel: "Resolved",
    main: "#34d399",
    subtle: "rgba(52, 211, 153, 0.16)",
    border: "#10b981",
  },
};

const ServicePage: React.FC = () => {
  const { dashboardId, serviceId } = useParams<{
    dashboardId: string;
    serviceId: string;
  }>();
  const { data: dashboard, isError } = useDashboard(dashboardId);
  const { data: serviceMap = { nodes: [], edges: [] } } = useServiceMap(
    dashboard?.id,
  );
  const navigate = useNavigate();
  const { show } = React.useContext(DialogContext);

  useEffect(() => {
    if (isError) {
      navigate("/dashboards");
    }
  }, [isError, navigate]);

  const selectedServiceId = serviceId;
  const rootNodeId = useMemo(
    () => selectRootServiceId(serviceMap.nodes, selectedServiceId),
    [selectedServiceId, serviceMap.nodes],
  );
  const serviceMapId = dashboard?.id;
  const { data: serviceEvents = [] } = useServiceEvents(serviceMapId, rootNodeId);
  const { data: alerts = [] } = useAlerts();
  const { data: integrations = [] } = useIntegrations();
  const [selectedKpiId, setSelectedKpiId] = React.useState<string | null>(null);
  const [selectedLinkedAlertId, setSelectedLinkedAlertId] = React.useState<string | null>(null);
  const {
    service,
    healthScore,
    riskForecast,
    slaTarget,
    sloTarget,
    criticalityPercent,
    revenuePercent,
    kpiBreakdown,
    selectedKpi,
    selectedLinkedAlert,
    selectedKpiLinkedAlerts,
    selectedLinkedAlertSeverityRows,
    selectedLinkedAlertTimelineRows,
    drawerTitle,
    drawerSubtitle,
    selectedKpiContributionPoints,
  } = useMemo(
    () =>
      buildServicePageViewData({
        nodes: serviceMap.nodes,
        rootNodeId,
        alerts,
        integrations,
        serviceEvents,
        selectedKpiId,
        selectedLinkedAlertId,
      }),
    [
      alerts,
      integrations,
      rootNodeId,
      selectedKpiId,
      selectedLinkedAlertId,
      serviceEvents,
      serviceMap.nodes,
    ],
  );

  useEffect(() => {
    setSelectedLinkedAlertId(null);
  }, [selectedKpiId]);

  useEffect(() => {
    if (!selectedLinkedAlertId) {
      return;
    }

    const exists = selectedKpiLinkedAlerts.some(
      (alert) => alert.id === selectedLinkedAlertId,
    );
    if (!exists) {
      setSelectedLinkedAlertId(null);
    }
  }, [selectedKpiLinkedAlerts, selectedLinkedAlertId]);

  const kpisWithoutAlerts = useMemo(() => {
    return kpiBreakdown.filter((kpi) => kpi.linkedAlerts === 0);
  }, [kpiBreakdown]);

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        padding: 2,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        boxSizing: "border-box",
        gap: 2.5,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <IconButton
          onClick={() => navigate(`/dashboards/${dashboardId}/services`)}
          size="small"
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: COLORS.grey[100] }} noWrap>
              {service?.label}
            </Typography>
            {service?.status && <TrafficLight status={service.status} size="md" />}
          </Stack>
          {service?.owner && (
            <Typography variant="body2" sx={{ mt: 0.25 }}>
              Owned by{" "}{service.owner}
            </Typography>
          )}
        </Box>
      </Stack>

      <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
        <Grid size={{xs:12, md:6, lg:3}} sx={{ display: "flex" }}>
          <Card
            variant="glass"
            sx={{ p: 2, minHeight: 152, height: "100%", flex: 1, display: "flex" }}
          >
            <Stack spacing={1.25} sx={{ width: "100%", justifyContent: "center" }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(34, 211, 238, 0.12)",
                    color: COLORS.primary[300],
                    "& .MuiSvgIcon-root": { fontSize: 16 },
                  }}
                >
                  <ShieldOutlinedIcon />
                </Box>
                <Typography variant="body2" sx={{ color: COLORS.grey[400] }}>
                  Health Score
                </Typography>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={2}>
                <HealthScore score={healthScore} size="md" />
                <Typography sx={{ fontSize: 26, fontWeight: 800, color: COLORS.grey[100] }}>
                  {healthScore}
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{xs: 12, md: 6, lg: 3}} sx={{ display: "flex" }}>
          <Card
            variant="glass"
            sx={{ p: 2, minHeight: 152, height: "100%", flex: 1, display: "flex" }}
          >
            <Stack spacing={1.25} sx={{ width: "100%", justifyContent: "center" }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(245, 158, 11, 0.12)",
                    color: "#fbbf24",
                    "& .MuiSvgIcon-root": { fontSize: 16 },
                  }}
                >
                  <TrendingUpOutlinedIcon />
                </Box>
                <Typography variant="body2" sx={{ color: COLORS.grey[400] }}>
                  Risk Forecast
                </Typography>
              </Stack>

              <Typography sx={{ fontSize: 26, fontWeight: 800, color: COLORS.grey[100] }}>
                {riskForecast}%
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                next 30 min
              </Typography>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{xs: 12, md: 6, lg: 3}} sx={{ display: "flex" }}>
          <Card
            variant="glass"
            sx={{ p: 2, minHeight: 152, height: "100%", flex: 1, display: "flex" }}
          >
            <Stack spacing={1.25} sx={{ width: "100%", justifyContent: "center" }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(34, 197, 94, 0.12)",
                    color: "#34d399",
                    "& .MuiSvgIcon-root": { fontSize: 16 },
                  }}
                >
                  <TargetOutlinedIcon />
                </Box>
                <Typography variant="body2" sx={{ color: COLORS.grey[400] }}>
                  SLA Target
                </Typography>
              </Stack>

              <Typography sx={{ fontSize: 26, fontWeight: 800, color: COLORS.grey[100] }}>
                {slaTarget}%
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                SLO: {sloTarget}%
              </Typography>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{xs: 12, md: 6, lg: 3}} sx={{ display: "flex" }}>
          <Card
            variant="glass"
            sx={{ p: 2, minHeight: 152, height: "100%", flex: 1, display: "flex" }}
          >
            <Stack spacing={1.25} sx={{ width: "100%", justifyContent: "center" }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(34, 211, 238, 0.12)",
                    color: COLORS.primary[300],
                    "& .MuiSvgIcon-root": { fontSize: 16 },
                  }}
                >
                  <ActivityOutlinedIcon />
                </Box>
                <Typography variant="body2" sx={{ color: COLORS.grey[400] }}>
                  Criticality
                </Typography>
              </Stack>

              <Typography sx={{ fontSize: 26, fontWeight: 800, color: COLORS.grey[100] }}>
                {criticalityPercent}%
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                Revenue: {revenuePercent}%
              </Typography>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 750, color: COLORS.grey[300] }}>
            Service Topology
          </Typography>
          <Stack direction="column" spacing={1.25} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={<AccountTreeRoundedIcon fontSize="small" />}
              onClick={() => {
                if (!serviceMapId || !rootNodeId) {
                  return;
                }
                show({
                  dialog: "manage-dependencies",
                  serviceMapId,
                  serviceId: rootNodeId,
                });
              }}
            >
              Manage
            </Button>
          </Stack>
        </Stack>

        <Card
          variant="glass"
          sx={{
            p: 0,
            display: "inline-flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <Box sx={{ px: 2, py: 1.25 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                Dependents above • dependencies below
              </Typography>
            </Stack>
          </Box>
          <Divider />
          <Box>
            {dashboard?.id && (
              <ServiceMapDiagram
                nodes={serviceMap.nodes}
                edges={serviceMap.edges}
                rootNodeId={rootNodeId}
                onNodeClick={(id) =>
                  navigate(`/dashboards/${dashboardId}/services/${id}`)
                }
              />
            )}
          </Box>
        </Card>
      </Box>

      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <EqualizerRoundedIcon sx={{ color: COLORS.primary[300], fontSize: 16 }} />
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: COLORS.grey[100] }}>
              KPI Health Breakdown
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
              {kpiBreakdown.length} KPIs drive this service health score
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<HubRoundedIcon fontSize="small" />}
              onClick={() => {
                if (!serviceMapId || !rootNodeId) {
                  return;
                }
                show({
                  dialog: "manage-kpi-alert-mappings",
                  serviceMapId,
                  serviceId: rootNodeId,
                });
              }}
            >
              Manage
            </Button>
          </Stack>
        </Stack>

        {kpisWithoutAlerts.length > 0 && (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{ mb: 1.5, borderColor: "rgba(251,191,36,0.45)" }}
          >
            <Typography sx={{ fontWeight: 700, color: "#fbbf24", mb: 0.25 }}>
              Missing KPI alert mapping
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.grey[300] }}>
              {kpisWithoutAlerts.length === 1
                ? `KPI without alerts: ${kpisWithoutAlerts[0].name}.`
                : `KPIs without alerts: ${kpisWithoutAlerts.map((kpi) => kpi.name).join(", ")}.`}
            </Typography>
          </Alert>
        )}

        <Stack spacing={1.5}>
          {kpiBreakdown.map((kpi) => (
            <Card
              key={kpi.id}
              variant="glass"
              onClick={() => {
                setSelectedKpiId(kpi.id);
              }}
              sx={{
                p: 1.75,
                cursor: "pointer",
                borderColor: selectedKpiId === kpi.id ? "rgba(56,189,248,0.45)" : undefined,
                boxShadow: selectedKpiId === kpi.id ? "0 0 0 1px rgba(56,189,248,0.25) inset" : undefined,
              }}
            >
              <Stack direction="row" spacing={1.4} alignItems="center">
                <HealthScore score={kpi.score} size="sm" />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 0.85, minHeight: 24 }}
                  >
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.grey[100] }} noWrap>
                      {kpi.name}
                    </Typography>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {kpi.alerts > 0 && (
                        <Stack direction="row" spacing={0.45} alignItems="center">
                          <WarningAmberRoundedIcon sx={{ fontSize: 14, color: "#fbbf24" }} />
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#fbbf24" }}>
                            {kpi.alerts} alerts
                          </Typography>
                        </Stack>
                      )}
                      <Typography
                        sx={{
                          fontSize: 18,
                          fontWeight: 800,
                          color: scoreColorForScore(kpi.score),
                        }}
                      >
                        {kpi.score}%
                      </Typography>
                      <ChevronRightRoundedIcon sx={{ color: COLORS.grey[600], fontSize: 18 }} />
                    </Stack>
                  </Stack>

                  <Box
                    sx={{
                      width: "100%",
                      height: 6,
                      borderRadius: 999,
                      background: "rgba(71,85,105,0.45)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${kpi.score}%`,
                        height: "100%",
                        borderRadius: 999,
                        background: scoreColorForScore(kpi.score),
                      }}
                    />
                  </Box>

                  <Stack direction="row" spacing={2} sx={{ mt: 0.9 }}>
                    <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                      Weight: {" "} {isCriticalKpiId(kpi.id) ? "Critical" : `${kpi.weightPercent}%`}
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                      Alerts linked: {" "} {kpi.linkedAlerts}
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                      Warning: {" "} {kpi.warning}
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                      Critical: {" "} {kpi.critical}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Card>
          ))}
        </Stack>
      </Box>

      <Drawer
        anchor="right"
        open={Boolean(selectedKpi)}
        onClose={() => {
          setSelectedLinkedAlertId(null);
          setSelectedKpiId(null);
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 460, md: 560 },
            borderLeft: "1px solid rgba(51,65,85,0.5)",
            background:
              "linear-gradient(180deg, rgba(6,14,40,0.98) 0%, rgba(2,8,28,0.99) 100%)",
            backdropFilter: "blur(8px)",
          },
        }}
        ModalProps={{
          BackdropProps: {
            sx: {
              backgroundColor: "rgba(2,6,23,0.45)",
              backdropFilter: "blur(2px)",
            },
          },
        }}
      >
        {selectedKpi && (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                pt: { xs: "calc(env(safe-area-inset-top) + 12px)", sm: 2.5 },
                pb: { xs: 1.5, sm: 2.5 },
                borderBottom: "1px solid rgba(51,65,85,0.5)",
              }}
            >
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  {selectedLinkedAlert && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedLinkedAlertId(null);
                      }}
                      sx={{ mt: 0.25 }}
                    >
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: { xs: 20, sm: 24 },
                        lineHeight: 1.2,
                        fontWeight: 800,
                        color: COLORS.grey[100],
                      }}
                    >
                      {drawerTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.grey[500], mt: 0.5 }}>
                      {drawerSubtitle}
                    </Typography>
                  </Box>
                </Stack>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedLinkedAlertId(null);
                    setSelectedKpiId(null);
                  }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>

            {!selectedLinkedAlert && (
              <Box
                key="kpi-drawer-content"
                sx={{
                  flex: 1,
                  minHeight: 0,
                  p: { xs: 2, sm: 3 },
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.4,
                  "& > *": {
                    flexShrink: 0,
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <HealthScore score={selectedKpi.score} size="md" />
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1.2} sx={{ mt: 1 }}>
                      <Card variant="glass" sx={{ p: 1.2, minWidth: 120 }}>
                        <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                          Weight
                        </Typography>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: COLORS.grey[100] }}>
                          {isCriticalKpiId(selectedKpi.id)
                            ? "Special"
                            : `${selectedKpi.weightPercent}%`}
                        </Typography>
                      </Card>
                      <Card variant="glass" sx={{ p: 1.2, minWidth: 120 }}>
                        <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                          Active alerts
                        </Typography>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: COLORS.grey[100] }}>
                          {selectedKpi.alerts}
                        </Typography>
                      </Card>
                    </Stack>
                  </Box>
                </Stack>

                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.grey[500], letterSpacing: 0.8, mb: 1 }}>
                    THRESHOLD DEFINITION
                  </Typography>
                  <Card variant="glass" sx={{ p: 1.6 }}>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ color: COLORS.grey[400] }}>Warning</Typography>
                        <Typography sx={{ color: COLORS.grey[100], fontWeight: 700 }}>
                          {selectedKpi.warning}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ color: COLORS.grey[400] }}>Critical</Typography>
                        <Typography sx={{ color: COLORS.grey[100], fontWeight: 700 }}>
                          {selectedKpi.critical}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Card>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.grey[500], letterSpacing: 0.8, mb: 1 }}>
                    LINKED ALARMS ({selectedKpiLinkedAlerts.length})
                  </Typography>
                  <Stack spacing={1}>
                    {selectedKpiLinkedAlerts.length === 0 && (
                      <Card variant="glass" sx={{ p: 1.6 }}>
                        <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                          No alerts linked to this KPI.
                        </Typography>
                      </Card>
                    )}
                    {selectedKpiLinkedAlerts.map((alert) => (
                      <Card
                        key={alert.id}
                        variant="glass"
                        onClick={() => {
                          setSelectedLinkedAlertId(alert.id);
                        }}
                        sx={{
                          p: { xs: 1.25, sm: 1.6 },
                          cursor: "pointer",
                          borderColor:
                            selectedLinkedAlertId === alert.id
                              ? "rgba(56,189,248,0.45)"
                              : "rgba(51,65,85,0.45)",
                        }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: 1.75,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "rgba(148,163,184,0.15)",
                              color: COLORS.grey[400],
                              flexShrink: 0,
                            }}
                          >
                            <StorageRoundedIcon sx={{ fontSize: 19 }} />
                          </Box>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack
                              direction="row"
                              spacing={0.8}
                              alignItems="center"
                              sx={{ minWidth: 0 }}
                            >
                              <Typography
                                sx={{
                                  fontSize: { xs: 13, sm: 14 },
                                  fontWeight: 750,
                                  color: COLORS.grey[100],
                                  minWidth: 0,
                                }}
                                noWrap
                              >
                                {alert.name}
                              </Typography>
                              <Chip
                                size="small"
                                label={alert.typeLabel}
                                color={alert.isCriticalType ? "error" : "info"}
                                variant={alert.isCriticalType ? "filled" : "outlined"}
                                sx={{
                                  height: 20,
                                  "& .MuiChip-label": {
                                    px: 0.9,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                  },
                                }}
                              />
                            </Stack>
                            <Typography variant="body2" sx={{ color: COLORS.grey[500], mt: 0.2 }} noWrap>
                              {alert.sourceLabel}
                            </Typography>
                            <Stack direction="row" spacing={1.6} sx={{ mt: 0.55 }} alignItems="center" flexWrap="wrap">
                              <Stack direction="row" spacing={0.45} alignItems="center">
                                <AccessTimeRoundedIcon sx={{ fontSize: 12, color: COLORS.grey[500] }} />
                                <Typography variant="body2" sx={{ color: COLORS.grey[500], fontSize: 12 }}>
                                  Weight: {alert.isCriticalType ? "N/A" : `${alert.weight}%`}
                                </Typography>
                              </Stack>
                              <Typography variant="body2" sx={{ color: COLORS.grey[500], fontSize: 12 }}>
                                Type: {alert.typeLabel}
                              </Typography>
                            </Stack>
                          </Box>

                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                            <Stack direction="row" spacing={0.35} alignItems="center">
                              <WarningAmberRoundedIcon sx={{ fontSize: 14, color: "#fbbf24" }} />
                              <Typography sx={{ color: "#fbbf24", fontWeight: 700, fontSize: 13 }}>
                                {alert.eventsCount}
                              </Typography>
                            </Stack>

                            <HealthScore score={alert.signalScore} size="md" />

                            <ChevronRightRoundedIcon sx={{ color: COLORS.grey[600], fontSize: 18 }} />
                          </Stack>
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.grey[500], letterSpacing: 0.8, mb: 1 }}>
                    HEALTH IMPACT CONTRIBUTION
                  </Typography>
                  <Card variant="glass" sx={{ p: 1.6 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                        Contribution to service health
                      </Typography>
                      <Typography sx={{ color: COLORS.grey[100], fontWeight: 700 }}>
                        {selectedKpiContributionPoints} pts
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        width: "100%",
                        height: 8,
                        borderRadius: 999,
                        background: "rgba(71,85,105,0.45)",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${selectedKpi.score}%`,
                          height: "100%",
                          borderRadius: 999,
                          background: scoreColorForScore(selectedKpi.score),
                        }}
                      />
                    </Box>
                  </Card>
                </Box>
              </Box>
            )}

            {selectedLinkedAlert && (
              <Box
                key={`linked-alert-drawer-content#${selectedLinkedAlert.id}`}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  p: { xs: 2, sm: 3 },
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.4,
                  "& > *": {
                    flexShrink: 0,
                  },
                }}
              >
                <Card variant="glass" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(148,163,184,0.16)",
                          color: COLORS.grey[300],
                          flexShrink: 0,
                        }}
                      >
                        <StorageRoundedIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: { xs: 16, sm: 18 },
                              lineHeight: 1.25,
                              fontWeight: 750,
                              color: COLORS.grey[100],
                              minWidth: 0,
                            }}
                            noWrap
                          >
                            {selectedLinkedAlert.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={selectedLinkedAlert.typeLabel}
                            color={selectedLinkedAlert.isCriticalType ? "error" : "info"}
                            variant={selectedLinkedAlert.isCriticalType ? "filled" : "outlined"}
                            sx={{
                              height: 20,
                              "& .MuiChip-label": {
                                px: 0.9,
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: 0.3,
                              },
                            }}
                          />
                        </Stack>
                        <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                          {drawerSubtitle}
                        </Typography>
                      </Box>
                    </Stack>

                    <Grid container spacing={1.2}>
                      <Grid size={{ xs: 6 }}>
                        <Card variant="glass" sx={{ p: 1.25 }}>
                          <Typography sx={{ color: COLORS.grey[500], fontSize: 11, letterSpacing: 0.8 }}>
                            WEIGHT
                          </Typography>
                          <Typography
                            sx={{
                              color: COLORS.grey[100],
                              fontSize: { xs: 20, sm: 22 },
                              lineHeight: 1.2,
                              fontWeight: 750,
                              mt: 0.6,
                            }}
                          >
                            {selectedLinkedAlert.isCriticalType
                              ? "N/A"
                              : `${selectedLinkedAlert.weight}%`}
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Card variant="glass" sx={{ p: 1.25 }}>
                          <Typography sx={{ color: COLORS.grey[500], fontSize: 11, letterSpacing: 0.8 }}>
                            TYPE
                          </Typography>
                          <Typography
                            sx={{
                              color: COLORS.grey[100],
                              fontSize: { xs: 20, sm: 22 },
                              lineHeight: 1.2,
                              fontWeight: 750,
                              mt: 0.6,
                            }}
                          >
                            {selectedLinkedAlert.typeLabel}
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  </Stack>
                </Card>

                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <WarningAmberRoundedIcon sx={{ fontSize: 16, color: "#fbbf24" }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.grey[500], letterSpacing: 0.8 }}>
                      SEVERITY BREAKDOWN
                    </Typography>
                  </Stack>
                  <Grid container spacing={1.2}>
                    {selectedLinkedAlertSeverityRows.map((row) => (
                      (() => {
                        const tone = timelineToneByStatus[row.status];
                        return (
                          <Grid key={row.status} size={{ xs: 6 }}>
                            <Card variant="glass" sx={{ p: 1.25 }}>
                              <Stack spacing={0.8} alignItems="center">
                                <Box
                                  sx={{
                                    px: 1.2,
                                    py: 0.4,
                                    borderRadius: 1.5,
                                    border: `1px solid ${tone.main}`,
                                    backgroundColor: tone.subtle,
                                  }}
                                >
                                  <Typography sx={{ color: tone.main, fontSize: { xs: 14, sm: 16 }, fontWeight: 700 }}>
                                    {tone.severityLabel}
                                  </Typography>
                                </Box>
                                <Typography sx={{ color: COLORS.grey[100], fontSize: { xs: 18, sm: 20 }, fontWeight: 750 }}>
                                  {row.count}
                                </Typography>
                              </Stack>
                            </Card>
                          </Grid>
                        );
                      })()
                    ))}
                    {selectedLinkedAlertSeverityRows.length === 0 && (
                      <Grid size={{ xs: 12 }}>
                        <Card variant="glass" sx={{ p: 1.6 }}>
                          <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                            No events available for this alert.
                          </Typography>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <TimelineRoundedIcon sx={{ fontSize: 16, color: COLORS.primary[300] }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.grey[500], letterSpacing: 0.8 }}>
                      EVENTS TIMELINE ({selectedLinkedAlertTimelineRows.length})
                    </Typography>
                  </Stack>

                  <Stack spacing={1}>
                    {selectedLinkedAlertTimelineRows.length === 0 && (
                      <Card variant="glass" sx={{ p: 1.6 }}>
                        <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
                          No alert events to display.
                        </Typography>
                      </Card>
                    )}
                    {selectedLinkedAlertTimelineRows.map((row) => (
                      (() => {
                        const tone = timelineToneByStatus[row.status];
                        return (
                          <Card
                            key={row.id}
                            variant="glass"
                            sx={{
                              p: 1.5,
                              borderLeft: `3px solid ${tone.border}`,
                            }}
                          >
                            <Stack spacing={1.1}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                  <Box
                                    sx={{
                                      px: 1,
                                      py: 0.28,
                                      borderRadius: 1.25,
                                      border: `1px solid ${tone.main}`,
                                      backgroundColor: tone.subtle,
                                    }}
                                  >
                                    <Typography sx={{ color: tone.main, fontWeight: 700, fontSize: 11 }}>
                                      {tone.severityLabel}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      px: 1,
                                      py: 0.28,
                                      borderRadius: 1.25,
                                      border: `1px solid ${tone.main}`,
                                      backgroundColor: tone.subtle,
                                    }}
                                  >
                                    <Typography sx={{ color: tone.main, fontWeight: 700, fontSize: 11 }}>
                                      {tone.stateLabel}
                                    </Typography>
                                  </Box>
                                </Stack>
                                <Typography sx={{ color: COLORS.grey[500], fontSize: 12, fontFamily: "monospace" }}>
                                  {row.timeLabel}
                                </Typography>
                              </Stack>

                              <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={1.5}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    sx={{
                                      color: COLORS.grey[100],
                                      fontSize: { xs: 14, sm: 16 },
                                      lineHeight: 1.25,
                                      fontWeight: 700,
                                    }}
                                    noWrap
                                  >
                                    {row.metricLabel}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: COLORS.grey[500], mt: 0.2 }}>
                                    {row.source}
                                  </Typography>
                                </Box>
                                <Stack alignItems="flex-end">
                                  <Typography
                                    sx={{
                                      color: COLORS.grey[100],
                                      fontSize: { xs: 14, sm: 16 },
                                      lineHeight: 1.25,
                                      fontWeight: 700,
                                    }}
                                    noWrap
                                  >
                                    {row.valueLabel}
                                  </Typography>
                                  <Stack direction="row" spacing={0.4} alignItems="center">
                                    <AccessTimeRoundedIcon sx={{ fontSize: 12, color: COLORS.grey[500] }} />
                                    <Typography sx={{ color: COLORS.grey[500], fontSize: 12 }}>
                                      {row.ageLabel}
                                    </Typography>
                                  </Stack>
                                </Stack>
                              </Stack>
                            </Stack>
                          </Card>
                        );
                      })()
                    ))}
                  </Stack>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default ServicePage;
